import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import {
  ChartBarIcon,
  ClockIcon,
  EyeIcon,
  ShareIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';

const AnalyticsContainer = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const AnalyticsHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const AnalyticsTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AnalyticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const AnalyticsCard = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  padding: 1rem;
  text-align: center;
`;

const AnalyticsValue = styled.div`
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const AnalyticsLabel = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`;

const TrendIndicator = styled.span`
  color: ${theme.colors.text.secondary};
`;

const DashboardAnalytics = ({ dashboardId }) => {
  const [analytics, setAnalytics] = useState({
    totalViews: 0,
    uniqueVisitors: 0,
    avgSessionTime: 0,
    lastAccessed: null,
    dataPoints: 0,
    updateFrequency: 30,
    uptime: 0,
    shares: 0
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (dashboardId) {
      fetchAnalytics();
      const interval = setInterval(fetchAnalytics, 60000); // Update every minute
      return () => clearInterval(interval);
    }
  }, [dashboardId]);

  const fetchAnalytics = async () => {
    try {
      // Simulate analytics data for now
      const mockAnalytics = {
        totalViews: Math.floor(Math.random() * 1000) + 150,
        uniqueVisitors: Math.floor(Math.random() * 50) + 25,
        avgSessionTime: Math.floor(Math.random() * 300) + 120, // seconds
        lastAccessed: new Date(Date.now() - Math.random() * 3600000), // within last hour
        dataPoints: Math.floor(Math.random() * 100) + 50,
        updateFrequency: 30,
        uptime: Math.random() * 100,
        shares: Math.floor(Math.random() * 10)
      };
      
      setAnalytics(mockAnalytics);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  const formatLastAccessed = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getUptimeTrend = (uptime) => {
    if (uptime > 95) return 'up';
    if (uptime < 80) return 'down';
    return 'stable';
  };

  if (loading) {
    return (
      <AnalyticsContainer>
        <AnalyticsHeader>
          <AnalyticsTitle>
            <ChartBarIcon className="w-5 h-5" />
            Dashboard Analytics
          </AnalyticsTitle>
        </AnalyticsHeader>
        <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}>
          Loading analytics...
        </div>
      </AnalyticsContainer>
    );
  }

  return (
    <AnalyticsContainer>
      <AnalyticsHeader>
        <AnalyticsTitle>
          <ChartBarIcon className="w-5 h-5" />
          Dashboard Analytics
        </AnalyticsTitle>
      </AnalyticsHeader>
      
      <AnalyticsGrid>
        <AnalyticsCard>
          <AnalyticsValue>
            <EyeIcon className="w-5 h-5" />
            {analytics.totalViews.toLocaleString()}
          </AnalyticsValue>
          <AnalyticsLabel>Total Views</AnalyticsLabel>
        </AnalyticsCard>
        
        <AnalyticsCard>
          <AnalyticsValue>
            {analytics.uniqueVisitors}
          </AnalyticsValue>
          <AnalyticsLabel>Unique Visitors</AnalyticsLabel>
        </AnalyticsCard>
        
        <AnalyticsCard>
          <AnalyticsValue>
            <ClockIcon className="w-5 h-5" />
            {formatDuration(analytics.avgSessionTime)}
          </AnalyticsValue>
          <AnalyticsLabel>Avg Session Time</AnalyticsLabel>
        </AnalyticsCard>
        
        <AnalyticsCard>
          <AnalyticsValue>
            {formatLastAccessed(analytics.lastAccessed)}
          </AnalyticsValue>
          <AnalyticsLabel>Last Accessed</AnalyticsLabel>
        </AnalyticsCard>
        
        <AnalyticsCard>
          <AnalyticsValue>
            {analytics.dataPoints}
          </AnalyticsValue>
          <AnalyticsLabel>Active Data Points</AnalyticsLabel>
        </AnalyticsCard>
        
        <AnalyticsCard>
          <AnalyticsValue>
            <TrendIndicator trend={getUptimeTrend(analytics.uptime)}>
              {getUptimeTrend(analytics.uptime) === 'up' ? 
                <ArrowTrendingUpIcon className="w-4 h-4" /> : 
                getUptimeTrend(analytics.uptime) === 'down' ?
                <ArrowTrendingDownIcon className="w-4 h-4" /> : null
              }
            </TrendIndicator>
            {analytics.uptime.toFixed(1)}%
          </AnalyticsValue>
          <AnalyticsLabel>Uptime</AnalyticsLabel>
        </AnalyticsCard>
        
        <AnalyticsCard>
          <AnalyticsValue>
            <ShareIcon className="w-5 h-5" />
            {analytics.shares}
          </AnalyticsValue>
          <AnalyticsLabel>Shares</AnalyticsLabel>
        </AnalyticsCard>
        
        <AnalyticsCard>
          <AnalyticsValue>
            {analytics.updateFrequency}s
          </AnalyticsValue>
          <AnalyticsLabel>Update Frequency</AnalyticsLabel>
        </AnalyticsCard>
      </AnalyticsGrid>
    </AnalyticsContainer>
  );
};

export default DashboardAnalytics;